import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  ChevronDown,
  ChevronUp,
  MessageCircle,
  MoreHorizontal,
  X,
  CheckCircle2,
  ShieldAlert,
  Loader2,
  Lightbulb,
  FileCode2,
  Copy,
  Trash2,
  GitPullRequest,
} from 'lucide-react';
import type { Finding, EducationContent } from '@nirvik/shared';
import { CATEGORY_LABELS } from '@nirvik/shared';
import { Markdown } from '@/components/shared/markdown';
import { cn } from '@/lib/utils';

const SEVERITY_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  critical: { bg: 'bg-red-500/10 dark:bg-red-500/10', text: 'text-red-700 dark:text-red-400', dot: 'bg-red-500' },
  high: { bg: 'bg-orange-500/10 dark:bg-orange-500/10', text: 'text-orange-700 dark:text-orange-400', dot: 'bg-orange-500' },
  medium: { bg: 'bg-yellow-500/10 dark:bg-yellow-500/10', text: 'text-yellow-700 dark:text-yellow-400', dot: 'bg-yellow-500' },
  low: { bg: 'bg-blue-500/10 dark:bg-blue-500/10', text: 'text-blue-700 dark:text-blue-400', dot: 'bg-blue-500' },
  info: { bg: 'bg-gray-500/10 dark:bg-gray-500/10', text: 'text-muted-foreground dark:text-muted-foreground', dot: 'bg-gray-500' },
};

// Status badge styles — `open` doesn't render (default state). The other
// statuses each get a distinct pill so the lifecycle is glanceable in
// the list view.
const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  'fix-merged':   { bg: 'bg-india-green/15',          text: 'text-india-green',                 label: 'Fix merged' },
  fixed:          { bg: 'bg-emerald-500/15',          text: 'text-emerald-700 dark:text-emerald-400', label: 'Fixed' },
  dismissed:      { bg: 'bg-gray-500/15',             text: 'text-muted-foreground',            label: 'Dismissed' },
  'accepted-risk':{ bg: 'bg-amber-500/15',            text: 'text-amber-700 dark:text-amber-400',    label: 'Accepted risk' },
};

interface FindingCardProps {
  finding: Finding;
  orgId?: string;
  portfolioId?: string;
  projectId?: string;
  onDismiss?: (findingId: string, action: 'dismissed' | 'accepted-risk' | 'fixed') => void;
  /** Hard-delete handler. Only rendered when `canDelete` is true. */
  onDelete?: (findingId: string) => void;
  /** Gates the destructive Delete option in the dropdown. Caller is
   *  expected to set this when the user is org/platform admin. */
  canDelete?: boolean;
}

export function FindingCard({ finding, orgId, portfolioId, projectId, onDismiss, onDelete, canDelete }: FindingCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [fetchedContent, setFetchedContent] = useState<EducationContent | null>(null);
  const [showEducation, setShowEducation] = useState(false);
  const [explainError, setExplainError] = useState('');
  const sev = SEVERITY_STYLES[finding.severity] ?? SEVERITY_STYLES.info;

  // Education content can be pre-populated by the education agent (on the
  // finding record) or fetched on-demand via /explain.
  const education: EducationContent | null =
    fetchedContent || finding.educationContent || null;

  const handleExplain = async () => {
    if (education) {
      setShowEducation((v) => !v);
      return;
    }

    if (!orgId || !portfolioId || !projectId) return;

    setExplaining(true);
    setExplainError('');

    try {
      const result = await apiClient.explainFinding(orgId, portfolioId, projectId, finding.findingId);
      const data = result.data as EducationContent | undefined;
      if (data && data.plainLanguage) {
        setFetchedContent(data);
        setShowEducation(true);
      } else {
        setExplainError('Could not get explanation.');
      }
    } catch (err) {
      setExplainError(err instanceof Error ? err.message : 'Failed to get explanation.');
    } finally {
      setExplaining(false);
    }
  };

  const categoryLabel = CATEGORY_LABELS[finding.category] || finding.category;

  // Agent-sourced findings may have extra fields not in the base Finding type.
  const agentFinding = finding as Finding & {
    filePath?: string;
    lineNumber?: number;
    codeSnippet?: string;
    confidence?: 'high' | 'medium' | 'low';
    cweId?: string;
    owaspCategory?: string;
    autoFixAvailable?: boolean;
    autoFixDiff?: string;
    llmModel?: string;
    fixMergedAt?: string | null;
    fixMergedSha?: string | null;
    fixMergedPrUrl?: string | null;
  };

  const statusStyle = STATUS_STYLES[finding.status];

  return (
    <Card className="bg-white dark:bg-card border border-gray-200 dark:border-border rounded-xl overflow-hidden">
      {/* Header - always visible */}
      <div className="flex items-start">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 p-4 flex items-start gap-3 text-left hover:bg-gray-50 dark:hover:bg-muted/30 transition-colors"
        >
          <div className={cn('h-2 w-2 rounded-full mt-2 flex-shrink-0', sev.dot)} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full capitalize', sev.bg, sev.text)}>
                {finding.severity}
              </span>
              {statusStyle && (
                <span className={cn('text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1', statusStyle.bg, statusStyle.text)}>
                  {finding.status === 'fix-merged' && <GitPullRequest className="h-3 w-3" />}
                  {finding.status === 'fixed' && <CheckCircle2 className="h-3 w-3" />}
                  {statusStyle.label}
                </span>
              )}
              <span className="text-xs text-muted-foreground dark:text-muted-foreground">{categoryLabel}</span>
              {agentFinding.cweId && (
                <span className="text-xs text-muted-foreground dark:text-muted-foreground">{agentFinding.cweId}</span>
              )}
              {agentFinding.confidence && (
                <span className="text-xs text-muted-foreground dark:text-muted-foreground">
                  {agentFinding.confidence} confidence
                </span>
              )}
              {agentFinding.llmModel && (
                <span className="text-xs text-muted-foreground dark:text-muted-foreground font-mono">
                  {agentFinding.llmModel}
                </span>
              )}
              {finding.scannerSource && (
                <span className="text-xs text-muted-foreground dark:text-muted-foreground">
                  via {finding.scannerSource}
                </span>
              )}
            </div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-foreground mt-1">{finding.title}</h3>
            {agentFinding.filePath && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <FileCode2 className="h-3 w-3" />
                {agentFinding.filePath}
                {agentFinding.lineNumber && `:${agentFinding.lineNumber}`}
              </p>
            )}
            {finding.evidence?.url && !agentFinding.filePath && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <FileCode2 className="h-3 w-3" />
                {finding.evidence.url}
              </p>
            )}
          </div>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground dark:text-muted-foreground flex-shrink-0" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground dark:text-muted-foreground flex-shrink-0" />
          )}
        </button>

        {/* Actions dropdown — dismiss / accept / mark-fixed for everyone,
            destructive Delete only when canDelete is true (org/platform admin). */}
        {(onDismiss || (onDelete && canDelete)) && (
          <div className="p-4 pl-0">
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Button variant="ghost" size="icon-xs">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onDismiss && (
                  <>
                    <DropdownMenuItem onClick={() => onDismiss(finding.findingId, 'dismissed')}>
                      <X className="h-4 w-4 mr-2 text-muted-foreground" />
                      Dismiss
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDismiss(finding.findingId, 'accepted-risk')}>
                      <ShieldAlert className="h-4 w-4 mr-2 text-muted-foreground" />
                      Accept Risk
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onDismiss(finding.findingId, 'fixed')}>
                      <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                      Mark as Fixed
                    </DropdownMenuItem>
                  </>
                )}
                {onDelete && canDelete && (
                  <>
                    {onDismiss && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      onClick={() => {
                        if (confirm(`Delete this finding permanently?\n\n"${finding.title}"\n\nThis cannot be undone.`)) {
                          onDelete(finding.findingId);
                        }
                      }}
                      className="text-red-600 dark:text-red-400 focus:text-red-700 dark:focus:text-red-300"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete finding
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-border pt-4 space-y-4">
          {/* fix-merged: link out to the PR that merged the fix.
              Shown above everything else because it's the most useful
              context for "why is this finding's status what it is?" */}
          {finding.status === 'fix-merged' && agentFinding.fixMergedPrUrl && (
            <div className="rounded-lg border border-india-green/30 bg-india-green/5 p-3 text-xs flex items-start gap-2">
              <GitPullRequest className="h-4 w-4 text-india-green flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-india-green">Fix merged — pending re-scan</p>
                <p className="text-muted-foreground mt-0.5">
                  A Trinetra-opened PR for this finding was merged on GitHub. The next scan
                  against the merged commit will promote it to <strong>Fixed</strong> if the
                  bug is gone, or reopen it if the patch didn't take.
                </p>
                <a
                  href={agentFinding.fixMergedPrUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-india-green hover:underline mt-1 inline-block font-mono"
                >
                  {agentFinding.fixMergedPrUrl.replace(/^https?:\/\/github\.com\//, '')}
                </a>
                {agentFinding.fixMergedSha && (
                  <span className="ml-2 text-muted-foreground">
                    · merged at <code className="font-mono">{agentFinding.fixMergedSha.slice(0, 7)}</code>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Explain button - top of expanded content */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleExplain}
              disabled={explaining}
              className={cn(
                'flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors',
                showEducation && education
                  ? 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-500/30'
                  : 'bg-gray-100 dark:bg-muted text-purple-600 dark:text-purple-400 hover:bg-gray-200 dark:hover:bg-muted hover:text-purple-700 dark:hover:text-purple-300',
                explaining && 'opacity-60 cursor-not-allowed'
              )}
            >
              {explaining ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <MessageCircle className="h-3.5 w-3.5" />
              )}
              {explaining
                ? 'Explaining...'
                : showEducation && education
                  ? 'Hide explanation'
                  : education
                    ? 'Show explanation'
                    : 'Explain this finding'}
            </button>

            {explainError && (
              <span className="text-xs text-red-600 dark:text-red-400">{explainError}</span>
            )}
          </div>

          {/* Unified education content — populated by the Education agent or
              fetched on-demand via the /explain endpoint. */}
          {showEducation && education && (
            <div className="bg-purple-50 dark:bg-purple-500/5 border border-purple-100 dark:border-purple-500/20 rounded-lg p-5 space-y-4">
              <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                <Lightbulb className="h-4 w-4" />
                <span className="text-xs font-bold uppercase tracking-wide">AI Explanation</span>
                {education.labels && education.labels.length > 0 && (
                  <div className="ml-auto flex flex-wrap gap-1">
                    {education.labels.map((label, i) => (
                      <span
                        key={i}
                        className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <p className="text-sm text-gray-700 dark:text-foreground">{education.plainLanguage}</p>

              {education.analogy && (
                <div className="bg-white/60 dark:bg-muted/50 rounded-md p-3">
                  <p className="text-xs font-semibold text-muted-foreground dark:text-muted-foreground uppercase tracking-wide mb-1">Think of it like...</p>
                  <p className="text-sm text-gray-700 dark:text-foreground italic">{education.analogy}</p>
                </div>
              )}

              {education.whyItMatters && (
                <div>
                  <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-1">Why it matters</p>
                  <p className="text-sm text-gray-700 dark:text-foreground">{education.whyItMatters}</p>
                </div>
              )}

              {education.risk && (
                <div>
                  <p className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wide mb-1">What could happen</p>
                  <p className="text-sm text-gray-700 dark:text-foreground">{education.risk}</p>
                </div>
              )}

              {education.howToFix && (
                <div>
                  <p className="text-xs font-semibold text-green-600 dark:text-emerald-400 uppercase tracking-wide mb-2">How to fix it</p>
                  <Markdown className="text-sm text-gray-700 dark:text-foreground">
                    {education.howToFix}
                  </Markdown>
                </div>
              )}

              {education.learnMore && education.learnMore.length > 0 && (
                <div className="pt-2 border-t border-purple-100 dark:border-purple-500/20">
                  <p className="text-xs font-semibold text-muted-foreground dark:text-muted-foreground uppercase tracking-wide mb-1">Learn more</p>
                  <ul className="space-y-1">
                    {education.learnMore.map((link, i) => (
                      <li key={i}>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-purple-600 dark:text-purple-300 hover:underline"
                        >
                          {link.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground dark:text-muted-foreground uppercase tracking-wider mb-1">Description</h4>
            <Markdown className="text-sm text-gray-700 dark:text-foreground">
              {finding.description}
            </Markdown>
          </div>

          {/* Code snippet (agent findings) */}
          {agentFinding.codeSnippet && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-xs font-semibold text-muted-foreground dark:text-muted-foreground uppercase tracking-wider">Vulnerable Code</h4>
                <button
                  onClick={() => navigator.clipboard.writeText(agentFinding.codeSnippet!)}
                  className="text-xs text-muted-foreground dark:text-muted-foreground hover:text-muted-foreground dark:hover:text-foreground flex items-center gap-1"
                >
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
              <pre className="bg-gray-50 dark:bg-background border border-gray-200 dark:border-border rounded-lg p-3 text-xs text-gray-700 dark:text-foreground overflow-x-auto font-mono">
                {agentFinding.codeSnippet}
              </pre>
            </div>
          )}

          {/* Evidence (scanner-sourced findings) */}
          {finding.evidence && (finding.evidence.parameter || finding.evidence.payload || finding.evidence.response) && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground dark:text-muted-foreground uppercase tracking-wider mb-1">Evidence</h4>
              <div className="bg-gray-50 dark:bg-background border border-gray-200 dark:border-border rounded-lg p-3 text-xs font-mono space-y-1">
                {finding.evidence.method && finding.evidence.url && (
                  <p className="text-muted-foreground dark:text-muted-foreground">
                    <span className="text-blue-600 dark:text-blue-400 font-semibold">{finding.evidence.method}</span> {finding.evidence.url}
                  </p>
                )}
                {finding.evidence.parameter && (
                  <p className="text-muted-foreground dark:text-muted-foreground">Parameter: <span className="text-orange-600 dark:text-orange-400">{finding.evidence.parameter}</span></p>
                )}
                {finding.evidence.payload && (
                  <p className="text-muted-foreground dark:text-muted-foreground">Payload: <span className="text-red-600 dark:text-red-400">{finding.evidence.payload}</span></p>
                )}
                {finding.evidence.response && (
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-border">
                    <p className="text-muted-foreground dark:text-muted-foreground mb-1">Response:</p>
                    <pre className="text-muted-foreground dark:text-muted-foreground whitespace-pre-wrap">{finding.evidence.response}</pre>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Remediation */}
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground dark:text-muted-foreground uppercase tracking-wider mb-1">How to Fix</h4>
            <div className="bg-emerald-50 dark:bg-emerald-500/5 border border-emerald-200 dark:border-emerald-500/20 rounded-lg p-3">
              {finding.remediation ? (
                <Markdown className="text-sm text-gray-700 dark:text-foreground">
                  {finding.remediation}
                </Markdown>
              ) : (
                <p className="text-sm text-gray-700 dark:text-foreground">No remediation steps available yet.</p>
              )}
            </div>
          </div>

          {/* Auto-fix diff */}
          {agentFinding.autoFixAvailable && agentFinding.autoFixDiff && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground dark:text-muted-foreground uppercase tracking-wider mb-1">Auto-Fix Patch</h4>
              <pre className="bg-gray-50 dark:bg-background border border-gray-200 dark:border-border rounded-lg p-3 text-xs text-gray-700 dark:text-foreground overflow-x-auto font-mono">
                {agentFinding.autoFixDiff}
              </pre>
            </div>
          )}

          {/* OWASP */}
          {agentFinding.owaspCategory && (
            <p className="text-xs text-muted-foreground dark:text-muted-foreground">
              OWASP: {agentFinding.owaspCategory}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
